import { get } from "lodash";
import { saveAs } from "file-saver";
import { actionChannel, call, put, select, take } from "redux-saga/effects";

import {
  getEntityCall,
  getEntitiesCall,
  createEntityCall,
  updateEntityCall
} from "../../services/api.js";

import { createAction } from "../actions/actionUtils.js";
import { base64ToArrayBuffer } from "../../services/apiUtils.js";
import { getLowercaseContentType } from "../../utils/responseUtils.js";
import { sanitiseAction } from "../../utils/utils.js";

import { AUTH_REFRESH_THRESHOLD } from "../../constants/authenticationConstants.js";
import { AUTHENTICATION } from "../reducers/authentication/authenticationConstants.js";
import { CONTENT_TYPE } from "../../constants/api.js";
import { API_REQUEST } from "../actions/actionConstants.js";

import { getExp } from "./authenticationSagas.js";

import { apiSuccess, showError, apiError } from "../reducers/api/apiActions.js";

import { latestActivity } from "../reducers/authentication/authenticationActions.js";

// -- STATE SELECTORS -- //

const getTokens = state => get(state, "authentication.tokens", null);

// -- FETCH SAGA -- //

export function* fetchEntity(apiCall, actionEntity, options = {}, action) {
  // console.log("fetchEntity:", action);

  // set relevant store state
  yield put(createAction(actionEntity.REQUEST));

  // extract or set options
  const { showErrorModal = true, isFatal = false } = options;

  // retrieve tokens from store
  const tokens = yield select(getTokens);

  // fetch!
  const { response, error } = yield call(apiCall, tokens, action.payload);

  if (response) {
    if (response.ok !== true) {
      console.warn("WARNING :: response.ok !== true");
      throw response;
    }

    // return ok responses that have no body - DELETE & LOGOUT are expected to be formed this way
    if (response.status === 204) {
      const data = { "@id": action.payload.href, status: 204 };

      yield put(createAction(actionEntity.RECEIVE, data));
      yield put(apiSuccess(action, data));

      return data;
    }

    // return other ok responses that have no body as we're not sure what to do with those at this point
    if (response.status !== 200 && response.status !== 201) {
      yield put(apiSuccess(action, response));
      return response;
    }

    // handle all other request type responses
    // converting to lower case as http header definitions are case insensitive
    const contentType = getLowercaseContentType(response);

    const downloadName = action.payload.downloadName || "sherpa-download";

    switch (contentType) {
      // valid hydra requests
      case CONTENT_TYPE.JSON:
      case CONTENT_TYPE.JSON_CHARSET:
      case CONTENT_TYPE.JSON_LD:
      case CONTENT_TYPE.JSON_LD_CHARSET:
        let json = yield response.json();
        json.requestAction = sanitiseAction(action);

        // send entity success for reducers
        yield put(createAction(actionEntity.RECEIVE, json));
        yield put(apiSuccess(action, json));

        return json;

      // pdf and txt requests
      case CONTENT_TYPE.PDF:
      case CONTENT_TYPE.PDF_CHARSET:
        const pdf_data = yield response.text();
        // decode the base64 data
        const arrBuffer = base64ToArrayBuffer(pdf_data);
        const pdfBlob = new Blob([arrBuffer], { type: contentType });
        saveAs(pdfBlob, `${downloadName}.pdf`);
        return true;

      case CONTENT_TYPE.TXT:
      case CONTENT_TYPE.TXT_CHARSET:
        const txt_data = yield response.text();
        const txtBlob = new Blob([txt_data], { type: contentType });
        saveAs(txtBlob, `${downloadName}.txt`);
        return true;

      // for other request type throw the response
      default:
        console.warn("WARNING :: No parsable response found");
        throw response;
    }
  } else {
    // send entity failure for reducers
    yield put(createAction(actionEntity.FAILURE, { status: error.status }));

    // handle error processing and modal display
    yield handleError(error, action, showErrorModal, isFatal);

    // deal with transient 5xx error(s), so the SPA does not require a hard refresh to re-attempt:
    // 502 Bad gateway, eg. API out of PHP-FPM workers, 503 Service unavailable, 504 Gateway timeout
    if (
      error?.status === 502 ||
      error?.status === 503 ||
      error?.status === 504
    ) {
      return { error };
    }

    throw error;
  }
}

// -- FETCH ERROR HANDLING SAGA -- //

export function* handleError(error, action, showErrorModal, isFatal) {
  // decode json if available
  const contentType = getLowercaseContentType(error);
  let json = null;

  if (
    contentType === CONTENT_TYPE.JSON_PROBLEM ||
    contentType === CONTENT_TYPE.JSON_PROBLEM_CHARSET ||
    contentType === CONTENT_TYPE.JSON_LD ||
    contentType === CONTENT_TYPE.JSON_LD_CHARSET ||
    contentType === CONTENT_TYPE.JSON ||
    contentType === CONTENT_TYPE.JSON_CHARSET
  ) {
    json = yield error.json();
  }

  const errorAction = apiError({ error, json, action }, isFatal);

  // send api failure for error reducer
  yield put(errorAction);

  switch (get(error, "status", null)) {
    // bad data request issues
    case 400:
    case 422:
      if (showErrorModal && json["@type"] !== "ConstraintViolation") {
        yield put(showError());
      } else {
        // these should be handled directly by the container components
      }
      break;

    // handle all other error types
    default:
      if (showErrorModal !== false) {
        yield put(showError());
      }
  }
}

// -- GENERIC FETCH SAGAS -- //

export function* genericEntitySaga(
  apiCall,
  entityAction,
  options = {},
  action
) {
  // console.log("genericEntitySaga", action, options);

  try {
    const result = yield call(
      fetchEntity,
      apiCall,
      entityAction,
      options,
      action
    );

    const { logActivity = true } = options;

    if (logActivity === true) {
      yield put(latestActivity());
    }

    return result;
  } catch (err) {
    throw err;
  }
}

export function* getEntitySaga(actionEntity, options, action) {
  const getCall = action.payload["@id"] ? getEntityCall : getEntitiesCall;

  const allOptions = {
    ...action.options,
    ...options
  };

  try {
    const result = yield call(
      genericEntitySaga,
      getCall,
      actionEntity,
      allOptions,
      action
    );

    if (!allOptions.autoComplete !== false) {
      yield put({ type: actionEntity.GET_COMPLETE });
    }

    return result;
  } catch (err) {
    console.error("getEntitySaga :: error ::", err);
  }
}

export function* createEntitySaga(actionEntity, options, action) {
  // console.log("createEntitySaga", actionEntity, options, action);
  try {
    const result = yield call(
      genericEntitySaga,
      createEntityCall,
      actionEntity,
      options,
      action
    );

    if (!options || options.autoComplete !== false) {
      yield put({ type: actionEntity.POST_COMPLETE });
    }

    return result;
  } catch (error) {
    console.error("createEntitySaga :: error ::", error);
    throw error;
  }
}

export function* updateEntitySaga(actionEntity, options, action) {
  try {
    const result = yield call(
      genericEntitySaga,
      updateEntityCall,
      actionEntity,
      options,
      action
    );
    yield put({ type: actionEntity.PUT_COMPLETE });

    return result;
  } catch (err) {
    console.error("updateEntitySaga :: error ::", err);
    throw err;
  }
}

// -- ROOT WATCHERS -- //

export function* watchDataRequestSagas() {
  // console.log("watchDataRequestSagas");

  const requestChan = yield actionChannel(API_REQUEST);

  while (true) {
    const exp = yield select(getExp);
    const currTime = Date.now();
    const timeLeft = exp * 1000 - currTime;

    if (timeLeft < AUTH_REFRESH_THRESHOLD) {
      yield take(AUTHENTICATION.AUTHORISED);
    }

    const { action } = yield take(requestChan);

    yield put({ ...action });

    // yield put(latestActivity()); // maybe this should be here maybe not, maybe should just live in genericEntity stuff ... not sure yet.
  }
}
